import { ANALYSIS_TIMEOUTS, AnalysisError, AsyncContext, bounded } from "./analysis-async";
import { default as MimeParser } from "emailjs-mime-parser";

export class HtmlUtils {

  static SINGLE_MSO_REGEX = /<!--\s*\[if\s+mso]\s*>\s*([\s\S]*?)\s*<!\[endif]\s*-->/i;
  static ALL_MSO_REGEX = /<!--\s*\[if\s+mso]\s*>\s*([\s\S]*?)\s*<!\[endif]\s*-->/gi;

  static SINGLE_NOT_MSO_REGEX = /<!--\s*\[if\s*!mso]\s*><!-->\s*([\s\S]*?)\s*<!--\s*<!\[endif]\s*-->/i;
  static ALL_NOT_MSO_REGEX = /<!--\s*\[if\s*!mso]\s*><!-->\s*([\s\S]*?)\s*<!--\s*<!\[endif]\s*-->/gi;

  /**
   * Create a temporary hidden iframe to parse the email body, because sometimes we couldn't get the applied styles directly
   * it needs to be embedded in iframe to compute them and be able to extract them.
   * @param emailBody
   */
  static async documentToIframeAsync(emailBody: string, context?: AsyncContext): Promise<Document> {
    const iframe = document.createElement('iframe');
    iframe.style.cssText = 'width:1px;height:1px;position:absolute;left:-9999px';
    // Email markup needs style calculation, never script execution.
    iframe.setAttribute('sandbox', 'allow-same-origin');
    let loaded: () => void;
    try {
      await bounded(() => new Promise<void>(resolve => {
        loaded = () => resolve();
        iframe.addEventListener('load', loaded, { once: true });
        iframe.srcdoc = emailBody;
        document.body.appendChild(iframe);
      }), ANALYSIS_TIMEOUTS.iframe, 'Email styles', context);
      context?.assertActive();
      if (!iframe.contentDocument) throw new AnalysisError('unavailable', 'Email document unavailable');
      return iframe.contentDocument;
    } catch (error) {
      iframe.remove();
      throw error;
    } finally {
      iframe.removeEventListener('load', loaded);
    }
  }

  static convertCSSToInline(style: CSSStyleDeclaration): string {
    if (!style) return "";
    return Array.from(style)
      .map((prop) => {
        let value = style.getPropertyValue(prop);
        if(style.getPropertyValue(prop).includes("rgb")) {
          let colorValue =  this.rgbToHex(style.getPropertyValue(prop));

          // Replace white colors to black, for proper display in UI
          if(colorValue.toLowerCase() == "#ffffff")
            value = "#000";
        }
        return `${prop}: ${value} !important;`;
      })
      .join(" ");
  }

  static rgbToHex(rgb: string): string {
    // Extract numbers using regex (matches: rgb(255, 0, 0) or rgba(255, 0, 0, 1))
    const match = rgb.match(/\d+/g);

    if (!match || match.length < 3) {
      return rgb; // Return original if not valid RGB
    }

    // Convert RGB to HEX
    const hex = `#${match.slice(0, 3) // Take only R, G, B
      .map((num) => parseInt(num, 10).toString(16).padStart(2, "0")) // Convert to hex
      .join("")}`;

    return hex.toUpperCase(); // Convert to uppercase for consistency
  }


  static extractHtmlFromMime(arrayBuffer: ArrayBuffer): string | null {
    const root = MimeParser(new Uint8Array(arrayBuffer));
    const htmlPart = this.findHtmlPart(root);
    if (!htmlPart) return null;

    const encoding = htmlPart.headers['content-transfer-encoding']?.[0]?.value?.toLowerCase() || '7bit';
    const buffer = htmlPart.content;

    switch (encoding) {
      case 'quoted-printable':
        return this.decodeQuotedPrintable(this.uint8ToString(buffer));
      case 'base64':
      case '7bit':
      case '8bit':
      default:
        // No further decoding needed — the parser already decoded base64 for you
        return this.uint8ToString(buffer);
    }
  }

  private static findHtmlPart(node: any): any | null {
    if (node.contentType?.value === 'text/html') return node;
    for (const child of node.childNodes || []) {
      const found = this.findHtmlPart(child);
      if (found) return found;
    }
    return null;
  }

  private static decodeQuotedPrintable(input: string): string {
    return input
      .replace(/=[\r\n]+/g, '') // soft line breaks
      .replace(/=([A-Fa-f0-9]{2})/g, (_, hex) =>
        String.fromCharCode(parseInt(hex, 16))
      );
  }

  private static uint8ToString(buffer: Uint8Array): string {
    return new TextDecoder('utf-8').decode(buffer);
  }

  // Extract <a> links from an MSO conditional block
  static extractAnchorsFromBlock(block: string): { href: string }[] {
    // Remove the conditional wrapper around the content
    let inner = block;
    const msoMatch = inner.match(this.SINGLE_MSO_REGEX);
    if (msoMatch) inner = msoMatch[1];
    else {
      const notMsoMatch = inner.match(this.SINGLE_NOT_MSO_REGEX);
      if (notMsoMatch) inner = notMsoMatch[1];
      else {
        // Fallback: try to remove any "[if ...]" opener and ender lines if present
        inner = inner
          .replace(/<!--\s*\[if\s+!?mso]\s*>\s*<!--?>?/i, '')
          .replace(/<!--?\s*<!\[endif\]\s*-->/i, '');
      }
    }

    const wrapped = `<div>${inner}</div>`;
    const parser = new DOMParser();
    const doc = parser.parseFromString(wrapped, 'text/html');

    return Array.from(doc.querySelectorAll('a')).map((a) => ({
      href: (a.getAttribute('href') || '').trim(),
    }));
  }

  // Strip out the MSO conditional wrappers to get clean HTML
  static stripConditionalWrappers(html: string): string {
    return html
      .replace(this.ALL_MSO_REGEX, (_m, inner) => inner)
      .replace(this.ALL_NOT_MSO_REGEX, (_m, inner) => inner);
  }

  static markAnchorIsConditional(html: string){
    // Match MSO blocks
    const msoBlocks = html.match(this.ALL_MSO_REGEX) || [];
    const notMsoBlocks = html.match(this.ALL_NOT_MSO_REGEX) || [];

    // Extract anchors from each block
    const msoLinks = msoBlocks.flatMap(block => this.extractAnchorsFromBlock(block));
    const notMsoLinks = notMsoBlocks.flatMap(block => this.extractAnchorsFromBlock(block));

    // Remove the conditional wrappers
    const cleanedHtml = this.stripConditionalWrappers(html);

    // Ensure we wrap with <body> for DOM parsing if missing
    const fullHtml = cleanedHtml.includes('<body') ? cleanedHtml : `<body>${cleanedHtml}</body>`;

    // Parse HTML using DOMParser
    const parser = new DOMParser();
    const doc = parser.parseFromString(fullHtml, 'text/html');

    // Annotate <a> tags
    const anchors = doc.querySelectorAll('a');

    anchors.forEach(anchor => {
      const href = (anchor.getAttribute('href') || '').trim();

      if (msoLinks.some(link => link.href === href)) {
        anchor.setAttribute('data-mso', 'true');
        anchor.setAttribute('is-conditional-mso', 'true');
      } else if (notMsoLinks.some(link => link.href === href)) {
        anchor.setAttribute('data-mso', 'false');
        anchor.setAttribute('is-conditional-mso', 'true');
      } else {
        anchor.setAttribute('is-conditional-mso', 'false');
      }
    });

    return doc.body.innerHTML;
  }

}
